import { NavLink } from "react-router";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Copy } from "lucide-react";
import t from "@/lang/lang";
import {
  Address,
  addressListLabel,
  isEmptyAddress,
  newAddress,
} from "@/services/backend/addresses/types/address";
import {
  UpdateBuildingRequest,
  useBuildingsUpdateMutation,
} from "@/services/backend/buildings/api/building-api";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { BuildingAddressSelect } from "@/services/backend/buildings/components/building-address-select";
import { Button } from "@/shared/components/ui/button";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { BuildingAddressCard } from "../../addresses/components/building-address-card";

export function BuildingAddressEditGroup({
  buildingId,
  customerId,
  name,
  address,
  addressKey,
  topRightContent,
  title,
  description,
}: {
  buildingId: string;
  customerId: string;
  name: string;
  address?: Address;
  addressKey?: string;
  topRightContent?: ReactNode;
  title?: string;
  description?: string;
}) {
  const [overwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
  const [request, setRequest] = useState<UpdateBuildingRequest>(() =>
    newRequest(buildingId, customerId, name, address),
  );
  const entityKey = `${buildingId}:${customerId}:${name}`;
  const previousEntityKey = useRef(entityKey);

  useEffect(() => {
    if (previousEntityKey.current === entityKey) {
      return;
    }
    previousEntityKey.current = entityKey;
    setRequest(newRequest(buildingId, customerId, name, address));
  }, [address, buildingId, customerId, entityKey, name]);

  const [saveBuilding, saveState] = useBuildingsUpdateMutation();

  const normalizedAddress = useMemo(
    () => request.address ?? newAddress(),
    [request.address],
  );

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    saveBuilding,
    saveState.isLoading,
    saveState.error,
    saveState.isSuccess,
    saveState.reset,
    500,
    {
      toastError: true,
    },
  );

  return (
    <div className="grid gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Dialog
            open={overwriteDialogOpen}
            onOpenChange={setOverwriteDialogOpen}
          >
            <CommonDialogTrigger
              icon={Copy}
              label={t("Adresse überschreiben")}
              variant="outline"
            />
            <DialogContent className="sm:max-w-2xl">
              <CommonDialogHeader
                title={t("Adresse überschreiben")}
                description={t(
                  "Wählen Sie eine bestehende Adresse aus. Diese Adresse wird in das aktuelle Gebäude übernommen.",
                )}
              />
              <div className="grid gap-4">
                <div className="grid gap-1">
                  <div className="text-muted-foreground text-sm">
                    {t("Aktuelle Adresse")}
                  </div>
                  <div className="text-sm font-medium">
                    {addressListLabel(normalizedAddress) || t("Keine Adresse")}
                  </div>
                </div>
                <BuildingAddressSelect
                  customerId={customerId}
                  currentAddress={normalizedAddress}
                  onAddressSelect={(nextAddress) => {
                    setRequest((current) => ({
                      ...current,
                      address: nextAddress,
                    }));
                    setOverwriteDialogOpen(false);
                  }}
                />
              </div>
            </DialogContent>
          </Dialog>
          {!isEmptyAddress(normalizedAddress) && addressKey && (
            <NavLink
              to={`/customers/${customerId}/buildings/addresses/${addressKey}`}
            >
              <Button variant="outline">{t("Adresse öffnen")}</Button>
            </NavLink>
          )}
        </div>
        {topRightContent ? (
          <div className="flex shrink-0 justify-end">{topRightContent}</div>
        ) : null}
      </div>
      <BuildingAddressCard
        buildingAddress={normalizedAddress}
        onBuildingAddressChange={(nextAddress) =>
          setRequest((current) => ({ ...current, address: nextAddress }))
        }
        title={title}
        description={
          description ??
          t(
            "Die Adresse des Gebäudes kann leer bleiben und später ergänzt werden.",
          )
        }
      />
    </div>
  );
}

function newRequest(
  buildingId: string,
  customerId: string,
  name: string,
  address?: Address,
): UpdateBuildingRequest {
  return {
    id: buildingId,
    name,
    address: address ?? newAddress(),
  };
}
